import { useCallback, useEffect, useMemo, useState } from "react"
import { Text } from "../../../components"
import { CATALOG_DESIGNS } from "../../catalog/data"
import { CrmRequest, PAGE_SIZE, ensureOk, formatDateTime, getErrorMessage } from "../api"
import { ConfirmButton, ListStatus, Pager } from "../components"

interface CatalogRatingComment {
    id: number;
    raiting: number;
    comment: string | null;
    created_at: string | null;
}

interface CatalogRatingGroup {
    design_id: string;
    design_title: string;
    color_id: string | null;
    color_label: string | null;
    average: number;
    count: number;
    latest_at: string | null;
    comments: CatalogRatingComment[];
}

const colorKey = (id: string, index: number) => `${id}-${index}`

const buildCatalogImageMap = () => {
    const map = new Map<string, string>()
    CATALOG_DESIGNS.forEach((design) => {
        map.set(`${design.id}:`, design.image)
        design.colors?.forEach((color, index) => {
            map.set(`${design.id}:${colorKey(color.id, index)}`, color.image)
        })
    })
    return map
}

const ratingStars = (rating: number) => {
    const rounded = Math.round(rating)
    return `${'★'.repeat(rounded)}${'☆'.repeat(Math.max(0, 5 - rounded))}`
}

export const CatalogRatingsTab = ({request}: {request: CrmRequest}) => {
    const images = useMemo(buildCatalogImageMap, [])
    const [offset, setOffset] = useState(0)
    const [items, setItems] = useState<CatalogRatingGroup[]>([])
    const [loading, setLoading] = useState(true)
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [actionError, setActionError] = useState<string | null>(null)

    const load = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const params = new URLSearchParams({offset: String(offset), limit: String(PAGE_SIZE)})
            const res = await request(`/crm/get-catalog-raitings?${params}`)
            if (!res) return
            await ensureOk(res, 'Не удалось загрузить оценки')
            const data = await res.json()
            setItems(data.groups ?? [])
        } catch (err) {
            setError(getErrorMessage(err, 'Не удалось загрузить оценки'))
        } finally {
            setLoading(false)
        }
    }, [request, offset])

    useEffect(() => {
        load()
    }, [load])

    const deleteAll = async () => {
        setBusy(true)
        setActionError(null)
        try {
            const res = await request('/crm/delete-catalog-raitings', {method: 'DELETE'})
            if (!res) return
            await ensureOk(res, 'Не удалось удалить оценки')
            setOffset(0)
            await load()
        } catch (err) {
            setActionError(getErrorMessage(err, 'Не удалось удалить оценки'))
        } finally {
            setBusy(false)
        }
    }

    const hasNext = items.length === PAGE_SIZE

    return (
        <div className="crm-list">
            <div className="crm-logs-toolbar">
                <button type="button" className="admin-edit-btn" disabled={loading} onClick={() => load()}>
                    <Text size="xs" color="lightGray">Обновить</Text>
                </button>
                <ConfirmButton label="Удалить все оценки" confirmLabel="Удалить все" disabled={busy || loading || items.length === 0} onConfirm={deleteAll} />
            </div>

            {actionError && <Text size="xs" color="accent">{actionError}</Text>}
            <ListStatus loading={loading} error={error} empty={items.length === 0} emptyText="Оценок пока нет" />

            {!loading && items.map((item) => {
                const image = images.get(`${item.design_id}:${item.color_id ?? ''}`)
                return (
                    <div className="crm-rating-row" key={`${item.design_id}-${item.color_id ?? 'default'}`}>
                        {image && <img className="crm-rating-thumb" src={image} alt="" />}
                        <div className="crm-rating-main">
                            <div className="crm-rating-head">
                                <div>
                                    <Text size="m" color="white">{item.design_title || item.design_id}</Text>
                                    <span className="admin-link-metric">
                                        {item.color_label ? `${item.color_label}${item.color_id ? ` · ${item.color_id}` : ''}` : item.design_id}
                                    </span>
                                </div>
                                <div className="crm-rating-summary">
                                    <span className="crm-rating-stars">{ratingStars(item.average)}</span>
                                    <span>{`${item.average.toFixed(1)} · ${item.count}`}</span>
                                </div>
                            </div>

                            <details className="crm-rating-comments">
                                <summary>{`Комментарии: ${item.comments.filter((comment) => comment.comment).length} из ${item.count}`}</summary>
                                <div className="crm-rating-comments-list">
                                    {item.comments.map((comment) => (
                                        <div className="crm-rating-comment-item" key={comment.id}>
                                            <div className="crm-rating-comment-head">
                                                <span className="crm-rating-stars">{ratingStars(comment.raiting)}</span>
                                                <span>{comment.created_at ? formatDateTime(comment.created_at) : 'Дата не записана'}</span>
                                            </div>
                                            {comment.comment
                                                ? <span className="crm-rating-comment">{comment.comment}</span>
                                                : <span className="admin-link-metric">Без комментария</span>}
                                        </div>
                                    ))}
                                </div>
                            </details>
                        </div>
                    </div>
                )
            })}

            <Pager offset={offset} hasNext={hasNext} loading={loading} onChange={setOffset} />
        </div>
    )
}
